/**
 * The close flow — a dedicated dialog, never a status dropdown.
 *
 * §6.2 puts the *commercial* close on the stage move to `DESIGN_WIN`, so this
 * dialog is normally reached for a loss, a no-decision, or a lapse. Losses can
 * be recorded from any stage.
 *
 * **Design win lapsed is offered only for a deal already `won`** (§5.1).
 * Without it there is no user path to record a lapse at all, and
 * `POST /api/programs/:id/cancel` becomes the only way that outcome can ever
 * be written. It means *the win expired without becoming production revenue* —
 * the reason code then says whether that was a cancelled program, an
 * indefinite delay, or simply no cause anyone recorded.
 *
 * **Re-closing a won deal states plainly that the earlier win is retained**,
 * and shows it. §5.5 makes closure an append-only table precisely so that is
 * true: the re-close writes a NEW row at the next sequence and the win
 * survives, which is what keeps a Q1 win rate from silently changing two years
 * later. People will otherwise assume they are erasing it and avoid the action.
 *
 * **Invalid — not a real opportunity** is offered only to a Manager or an
 * Admin (decision 12), decided by `canChangeOpportunityStatus` from
 * `@menlo/shared` — the same predicate the API refuses with. It is how a deal
 * that should never have existed is set aside: opportunities are never
 * deleted. It needs the reason code and a note saying why.
 */

import { useMemo, useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { reasonCodesFor, useReferenceData } from '@/lib/reference';
import { useApiMutation } from '@/lib/queries';
import { formatDate, formatMoney, humanize, today } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCanChangeOpportunityStatus } from '@/auth/useMe';
import type { ClosureRow } from '@menlo/shared';

const OUTCOMES = [
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'no_decision', label: 'No decision' },
] as const;

export function CloseDialog({
  opportunity,
  closures,
  onClose,
  onClosed,
}: {
  opportunity: Record<string, unknown>;
  closures: ClosureRow[];
  onClose: () => void;
  onClosed: () => void;
}): React.ReactElement {
  const reference = useReferenceData();
  const canChangeStatus = useCanChangeOpportunityStatus();
  const mayMarkInvalid = canChangeStatus(
    {
      owner_user_id: (opportunity['owner_user_id'] as string | null) ?? null,
      fae_user_id: (opportunity['fae_user_id'] as string | null) ?? null,
      status: String(opportunity['status']),
    },
    'invalid',
  );
  const alreadyWon = opportunity['status'] === 'won';
  const previousWin = closures.find((closure) => closure.outcome === 'won');

  const [outcome, setOutcome] = useState<string>(alreadyWon ? 'lost' : 'lost');
  const [closeDate, setCloseDate] = useState(today());
  const [reason, setReason] = useState('');
  const [revenue, setRevenue] = useState(String(opportunity['annual_revenue_customer'] ?? '0'));
  const [competitorId, setCompetitorId] = useState('');
  const [competitorPart, setCompetitorPart] = useState('');
  const [priceDelta, setPriceDelta] = useState('');
  const [whatWould, setWhatWould] = useState('');
  const [lessons, setLessons] = useState('');
  const [note, setNote] = useState('');

  const reasons = useMemo(() => reasonCodesFor(reference, outcome), [reference, outcome]);
  const grouped = useMemo(() => {
    const groups = new Map<string, typeof reasons>();
    for (const code of reasons) {
      const bucket = groups.get(code.category) ?? [];
      bucket.push(code);
      groups.set(code.category, bucket);
    }
    return [...groups.entries()];
  }, [reasons]);

  const close = useApiMutation<{ closure_sequence: number }, void>(
    () => ({
      path: `/api/opportunities/${String(opportunity['id'])}/close`,
      method: 'POST',
      body: {
        outcome,
        close_date: closeDate,
        primary_reason_code: reason,
        ...(outcome === 'won' ? { customer_annual_revenue: revenue } : {}),
        ...(competitorId === '' ? {} : { competitor_id: competitorId }),
        ...(competitorPart === '' ? {} : { competitor_part_number: competitorPart }),
        ...(priceDelta === '' ? {} : { price_delta_pct: Number(priceDelta) }),
        ...(whatWould.trim() === '' ? {} : { what_would_have_changed_it: whatWould.trim() }),
        ...(lessons.trim() === '' ? {} : { lessons_learned: lessons.trim() }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      },
    }),
    { onSuccess: onClosed },
  );

  const isLoss = outcome === 'lost';
  const isInvalid = outcome === 'invalid';
  const ready = reason !== '' && (!isInvalid || note.trim() !== '');

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Close ${String(opportunity['opp_number'] ?? '')}`}
      description="The reason code is what every win/loss report in this product is built from."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => close.mutate()} disabled={!ready || close.isPending}>
            {close.isPending ? 'Recording…' : 'Record the closure'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {alreadyWon && previousWin !== undefined && (
          <div className="rounded-md border border-emerald-600/40 bg-emerald-50 p-3 text-xs text-emerald-950">
            <p className="font-medium">The existing win is kept.</p>
            <p className="mt-1">
              This deal was won on {formatDate(previousWin.close_date)} for{' '}
              {formatMoney(previousWin.actual_annual_revenue)} net (
              {previousWin.primary_reason_label}). Closing it again appends a new record at sequence{' '}
              {closures.length + 1}; the win stays in the quarter it happened and every historical
              report keeps counting it.
            </p>
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-medium">Outcome</p>
          <div className="flex flex-wrap gap-2">
            {OUTCOMES.map((option) => (
              <OutcomeButton
                key={option.value}
                label={option.label}
                selected={outcome === option.value}
                onSelect={() => {
                  setOutcome(option.value);
                  setReason('');
                }}
              />
            ))}
            {alreadyWon && (
              <OutcomeButton
                label="Design win lapsed"
                selected={outcome === 'design_win_lapsed'}
                onSelect={() => {
                  setOutcome('design_win_lapsed');
                  setReason('');
                }}
                hint="The win expired without becoming production revenue."
              />
            )}
            {mayMarkInvalid && (
              <OutcomeButton
                label="Invalid — not a real opportunity"
                selected={isInvalid}
                onSelect={() => {
                  setOutcome('invalid');
                  setReason('');
                }}
                hint="Imported by mistake, a duplicate or a test record. Hidden everywhere; never deleted."
              />
            )}
          </div>
          {isInvalid && (
            <p className="mt-2 text-xs text-muted-foreground">
              The deal is kept, and leaves the list, the pipeline, the forecast and every report.
              Choosing Invalid in the list&rsquo;s status filter finds it again. Only a Manager or
              an Admin can reopen it.
            </p>
          )}
          {outcome === 'design_win_lapsed' && (
            <p className="mt-2 text-xs text-muted-foreground">
              Offered because this deal is already won. It means the win expired without becoming
              production revenue — pick the reason that fits: a cancelled programme, an indefinite
              delay, or no cause anyone recorded.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Close date">
            <Input
              type="date"
              value={closeDate}
              onChange={(event) => setCloseDate(event.target.value)}
            />
          </Field>
          {outcome === 'won' && (
            <Field
              label="Annual revenue (customer basis)"
              hint="Stored on the Menlo net basis with the factor that produced it."
            >
              <Input
                inputMode="decimal"
                value={revenue}
                onChange={(event) => setRevenue(event.target.value)}
              />
            </Field>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">
            Primary reason <span className="text-destructive">*</span>
          </p>
          {grouped.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No selectable reason codes apply to this outcome.
            </p>
          )}
          <div className="space-y-3">
            {grouped.map(([category, codes]) => (
              <div key={category}>
                <p className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  {humanize(category)}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {codes.map((code) => (
                    <button
                      key={code.code}
                      type="button"
                      aria-pressed={reason === code.code}
                      onClick={() => setReason(code.code)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs',
                        reason === code.code
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input hover:bg-accent',
                      )}
                    >
                      {code.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {isInvalid && (
          <Field
            label="Why is this not a real opportunity? (required)"
            hint="Kept with the deal's history. It is what anyone who finds it later will read."
          >
            <Textarea
              aria-label="Why is this not a real opportunity?"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
        )}

        {isLoss && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Competitor">
              <Select
                value={competitorId}
                onChange={(event) => setCompetitorId(event.target.value)}
              >
                <option value="">—</option>
                {reference.competitors.map((competitor) => (
                  <option key={competitor.id} value={competitor.id}>
                    {competitor.name} ({humanize(competitor.technology_type)})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Competitor part number">
              <Input
                value={competitorPart}
                onChange={(event) => setCompetitorPart(event.target.value)}
              />
            </Field>
          </div>
        )}

        {!isInvalid && (
          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm">Optional detail</summary>
            <div className="mt-3 space-y-3">
              <Field label="Price delta (%)" hint="Negative means we were cheaper.">
                <Input
                  inputMode="decimal"
                  value={priceDelta}
                  onChange={(event) => setPriceDelta(event.target.value)}
                />
              </Field>
              <Field label="What would have changed it?">
                <Textarea
                  value={whatWould}
                  onChange={(event) => setWhatWould(event.target.value)}
                />
              </Field>
              <Field
                label="Lessons learned"
                hint="This and the field above are the only closure text an Admin can redact later — everything else on a closure is permanent."
              >
                <Textarea value={lessons} onChange={(event) => setLessons(event.target.value)} />
              </Field>
            </div>
          </details>
        )}

        <p className="text-xs text-muted-foreground">
          <Badge variant="outline">Append-only</Badge> Closures are never overwritten. Reopening
          this deal later writes nothing here; a re-close appends.
        </p>
      </div>
    </Dialog>
  );
}

function OutcomeButton({
  label,
  selected,
  onSelect,
  hint,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  hint?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={hint}
      onClick={onSelect}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm',
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input hover:bg-accent',
      )}
    >
      {label}
    </button>
  );
}
